import React, {
    type ClipboardEvent,
    type InputHTMLAttributes,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useDebounce } from 'react-use';

import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { set2faResetCode } from '@app/store/slices';

export enum SecurityCodeInputVariants {
    Primary,
    // midSpaced variant to cater for longer digit codes & the spacing in the middle
    MidSpaced,
}

interface SecurityCodeInputProps extends InputHTMLAttributes<HTMLInputElement> {
    setCompleteCode: (code: string) => void;
    classes?: string;
    digitCount?: number;
    isInvalid?: boolean;
    errorLabel?: string;
    digitOnly?: boolean;
    variant?: SecurityCodeInputVariants;
    onAutoSubmit?: () => void;
}

const regexForNumbersOnly = /^[0-9\b]+$/;
const defaultLength = 4;

enum CursorLocation {
    After = 'After',
    Before = 'Before',
    Selected = 'Selected',
}

const SecurityCodeInput = ({
    setCompleteCode,
    classes,
    digitCount = defaultLength,
    isInvalid = false,
    variant = SecurityCodeInputVariants.Primary,
    errorLabel,
    digitOnly = false,
    onAutoSubmit,
    ...props
}: SecurityCodeInputProps) => {
    const resetCode = useAppSelector((rootState) => rootState.twoFactorAuth.resetCode);
    const [codeValues, setCodeValues] = useState<string[]>(Array(digitCount).fill(''));
    const [invalid, setInvalid] = useState(false);
    const codeItemRefs = useRef<Array<HTMLInputElement>>([]);

    const dispatch = useAppDispatch();

    // Add array of ref's for inputs
    useEffect(() => {
        codeItemRefs.current = codeItemRefs.current.slice(0, Number(digitCount));
    }, [digitCount]);

    useEffect(() => {
        setInvalid(isInvalid);
    }, [isInvalid]);

    const classNames = useMemo(() => {
        let classNames = 'yat-input yat-input--code';
        if (classes) classNames += ` ${classes}`;
        if (invalid) classNames += ` yat-input--invalid`;
        if (digitCount > defaultLength) classNames += ` yat-input--code-long`;
        return classNames;
    }, [classes, invalid, digitCount]);

    function getCursorPosition(element: HTMLInputElement): CursorLocation {
        const cursorStartPosition = element.selectionStart;
        const cursorEndPosition = element.selectionEnd;

        if (cursorStartPosition === 0 && cursorEndPosition === 0) {
            return CursorLocation.Before;
        } else if (cursorStartPosition === 1 && cursorEndPosition === 1) {
            return CursorLocation.After;
        } else {
            return CursorLocation.Selected;
        }
    }

    function hasAllDigits(digits: string[], digitCount = 6) {
        return digits.filter((d) => d !== '').length === digitCount;
    }

    const handleFocus = useCallback(
        (e) => {
            if (invalid) {
                setInvalid(false);
                setCodeValues(Array(digitCount).fill(''));
            }

            if (e.target.value !== '') {
                e.currentTarget.select();
            }
        },
        [invalid, digitCount],
    );

    const itemsFilled = (arr) => {
        return !arr.find((d) => d === '');
    };

    useEffect(() => {
        if (itemsFilled(codeValues)) {
            const code = codeValues.join('');
            setCompleteCode(code);
        }
    }, [codeValues, onAutoSubmit, setCompleteCode]);

    useDebounce(
        () => {
            if (hasAllDigits(codeValues)) {
                onAutoSubmit?.();
            }
        },
        100,
        [codeValues],
    );

    useEffect(() => {
        if (!resetCode) {
            return;
        }
        setCodeValues(Array(digitCount).fill(''));
        dispatch(set2faResetCode(false));
        codeItemRefs.current[0]?.focus();
    }, [resetCode, dispatch, digitCount]);

    const inputSpacerClass = useMemo(() => {
        if (variant === SecurityCodeInputVariants.MidSpaced && digitCount % 2 === 0) {
            // only if it's an even number of digits
            return ' yat-input--code-spaced';
        }
        return '';
    }, [variant, digitCount]);

    const renderInputs = useMemo(() => {
        const inputs: React.ReactElement[] = [];

        const handleChange = (e, i) => {
            const value = e.target.value;
            const digits = [...codeValues];
            if (value === '' || regexForNumbersOnly.test(value)) {
                digits[i] = value;
            }
            setCodeValues([...digits]);
            if (i !== digitCount - 1 && regexForNumbersOnly.test(value)) {
                codeItemRefs.current[i + 1].focus();
            }
        };

        const handleKeyDown = (e, i) => {
            const digits = [...codeValues];

            const cursorPosition = getCursorPosition(codeItemRefs.current[i]);

            if (e.key === 'Backspace' && i !== 0) {
                // If cursor after digit or digit selected, backspace normally
                if (cursorPosition === CursorLocation.After || cursorPosition === CursorLocation.Selected) {
                    digits[i] = '';
                } else {
                    // If cursor before digit, backspace previous digit
                    digits[i - 1] = '';
                    codeItemRefs.current[i - 1].focus();
                }
                setCodeValues([...digits]);
            }

            if (e.key === 'ArrowLeft' && i > 0) {
                (codeItemRefs.current[i]?.previousSibling as HTMLInputElement).focus();
            }

            if (e.key === 'ArrowRight' && i < digitCount - 1) {
                (codeItemRefs.current[i]?.nextSibling as HTMLInputElement).focus();
            }

            if (e.key === 'Delete' && i < digitCount - 1 && digits[i] === '') {
                // checking for this, so it only deletes the sibling if it's empty itself
                // this will behave like a normal delete if filled but delete the sibling if empty
                // (to avoid it deleting itself AND the sibling depending on if cursor is before/after the number, felt buggy)
                digits[i + 1] = '';
                setCodeValues([...digits]);
                codeItemRefs.current[i].focus();
            }
        };

        const handlePaste = (event: ClipboardEvent<HTMLInputElement>, index: number) => {
            event.preventDefault();
            const clipboardData = event.clipboardData.getData('Text');

            if (regexForNumbersOnly.test(clipboardData)) {
                const digits = clipboardData.split('');

                const posFromIndex = index + (digits.length - 1); // so the last filled input is focused regardless of where you paste from
                const posAfterPaste = posFromIndex < digitCount ? posFromIndex : digitCount - 1;

                digits.forEach((digit, loopI) => {
                    if (index + loopI < digitCount) {
                        setCodeValues((codes) =>
                            codes.map((code, i) => (i === index + loopI ? +digit : code).toString()),
                        );
                        (codeItemRefs.current[posAfterPaste] as HTMLInputElement).focus();
                    }
                });
            }
        };

        if (digitCount > 0) {
            for (let i = 0; i < digitCount; i++) {
                const classes =
                    inputSpacerClass && i === digitCount / 2 - 1 ? classNames + inputSpacerClass : classNames;

                inputs.push(
                    <input
                        {...props}
                        key={i}
                        onFocus={(e) => handleFocus(e)}
                        autoFocus={i === 0}
                        ref={(el: HTMLInputElement) => {
                            codeItemRefs.current[i] = el;
                        }}
                        className={classes}
                        maxLength={1}
                        autoComplete="off"
                        type={digitOnly ? 'tel' : 'text'}
                        value={codeValues[i]}
                        onChange={(e) => handleChange(e, i)}
                        onKeyDown={(e) => handleKeyDown(e, i)}
                        onPaste={(e) => handlePaste(e, i)}
                    />,
                );
            }
        }
        return inputs;
    }, [digitCount, codeValues, inputSpacerClass, classNames, props, digitOnly, handleFocus]);

    return (
        <>
            <div key={digitCount.toString()} className="yat-security-code-input">
                {renderInputs}
            </div>
            {invalid && errorLabel && <div className="yat-input__error-label">{errorLabel}</div>}
        </>
    );
};

export default SecurityCodeInput;
